import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  AuthenticationProviderType,
  AuthenticationProviderVarieties,
} from "@vviinn/user-managment-client-ts";

import { AuthForm } from "../wrappers";
import { useApi } from "../../api";
import {
  useAppDispatch,
  registrate,
  authentificate,
  getAuthenticationProviders,
} from "../../store";
import {
  FormStep,
  registerSteps,
  getStepValue,
  handleResetStep,
  getRecaptchaValue,
} from "../general";
import { useAuth, isEmailVerified } from "../hooks";
import { DEFAULT_ERROR_WITH_SUGGESTION_MESSAGE } from "../../ui/constants";

type ErrorMessagesKeys = "default" | "auth/email-already-in-use";

const errorMessages: Record<ErrorMessagesKeys, string> = {
  default: DEFAULT_ERROR_WITH_SUGGESTION_MESSAGE,
  "auth/email-already-in-use": "User with this email address already exists",
};

export const RegisterForm: FC<React.PropsWithChildren> = () => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { getApi, getAnonymousApi } = useApi();
  const { user } = useAuth();

  useEffect(() => {
    // An enterprise SSO account is verified by its identity provider —
    // sending it a verification email would be spurious.
    if (!user || isEmailVerified(user)) return;

    const sendVerificationEmail = async () => {
      const api = await getApi();
      if (!api) return;

      try {
        await api.sendVerificationEmail();
      } catch (e) {
        setErrorMessage(DEFAULT_ERROR_WITH_SUGGESTION_MESSAGE);
      } finally {
        setLoading(false);
      }
    };
    sendVerificationEmail();
  }, [user, getApi, navigate]);

  const handleIndividualRegister = async (formSteps: FormStep[]) => {
    const recaptchaToken = getRecaptchaValue(setErrorMessage)();
    if (!recaptchaToken) return;
    setErrorMessage("");

    setLoading(true);
    try {
      const email = getStepValue(formSteps, "email")?.toString();
      const password = getStepValue(formSteps, "password")?.toString();
      if (!email || !password) return;

      const registerData = { email, password };
      const registrationResult = await dispatch(registrate(registerData));

      if ("error" in registrationResult) {
        let errorMessageCode: ErrorMessagesKeys = registrationResult.error
          .code as ErrorMessagesKeys;
        if (!errorMessages[errorMessageCode]) {
          errorMessageCode = "default";
        }

        setErrorMessage(errorMessages[errorMessageCode]);
        setLoading(false);
      } else {
        registerSteps.map(handleResetStep);
      }
    } catch (error) {
      setErrorMessage(DEFAULT_ERROR_WITH_SUGGESTION_MESSAGE);
    }
  };

  const getConfigs = async (
    email: string,
    recaptchaToken: string,
  ): Promise<AuthenticationProviderVarieties> => {
    const api = await getAnonymousApi();
    const value = { email, recaptchaToken };
    const result = await dispatch(
      await getAuthenticationProviders({ api, value }),
    );
    const data = result.payload as AuthenticationProviderVarieties[];
    return data[0];
  };

  // Signing in via an SSO provider that's already linked to this email —
  // used both for an existing SSO account and for a new email whose domain
  // matches a company's SSO domains. Firebase creates the account on first
  // signInWithPopup, so there's no separate "SSO register" thunk.
  const handleLogin = async (
    email?: string,
    password?: string,
    providerId?: string,
    providerType?: AuthenticationProviderType,
  ) => {
    setErrorMessage("");
    setLoading(true);
    try {
      const loginResult = await dispatch(
        authentificate({ email, password, providerId, providerType }),
      );

      if ("error" in loginResult) {
        let errorMessageCode: ErrorMessagesKeys = loginResult.error
          .code as ErrorMessagesKeys;
        if (!errorMessages[errorMessageCode]) {
          errorMessageCode = "default";
        }

        setErrorMessage(errorMessages[errorMessageCode]);
      }
    } catch (error) {
      setErrorMessage(DEFAULT_ERROR_WITH_SUGGESTION_MESSAGE);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthForm
      submitTitle="Register"
      registerSubmitCallback={handleIndividualRegister}
      loginSubmitCallback={handleLogin}
      getConfigs={getConfigs}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      steps={registerSteps}
      isLoading={loading}
      needRecaptcha={true}
      needTermsAndConditions={true}
    />
  );
};
